import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LanguageService } from '../../../services/i18n/language';
import { SupplierDocumentService, SupplierShipmentDocument, SupplierShipmentDocumentItem } from '../../../services/api/supplier-document';
import { Subscription } from 'rxjs';

/**
 * Commercial Invoice + Packing List по перевозке (вкладка «Поставка» у грузовладельца,
 * «Поставщик» у поставщика). Редактирование доступно только поставщику ({@code readonly=false}),
 * грузовладелец видит то же самое в режиме просмотра. Оба PDF рендерятся на клиенте из одних
 * и тех же данных через html2pdf (глобально подключён в index.html).
 *
 * Сами PDF-шаблоны (Commercial Invoice / Packing List) намеренно остаются на английском
 * независимо от языка интерфейса — это международный торговый документ, а не UI-текст.
 */
@Component({
  selector: 'app-supplier-document',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './supplier-document.html',
  styleUrl: './supplier-document.scss'
})
export class SupplierDocumentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() calculationId!: number;
  @Input() readonlyMode = false;

  @ViewChild('ciTemplate') ciTemplate!: ElementRef;
  @ViewChild('plTemplate') plTemplate!: ElementRef;

  document: SupplierShipmentDocument | null = null;
  loading = false;
  saving = false;
  error: string | null = null;
  savedMessage: string | null = null;
  expanded = false;

  toggleTitleText = 'Документы поставки (CI / PL)';
  loadingText = 'Загрузка...';
  hintEditableText = 'Часть полей заполнена автоматически из расчёта — проверьте и дополните недостающее (вес, количество мест, объём, номера документов), затем сохраните.';
  hintReadonlyNotSavedText = 'Поставщик ещё не сохранил документы по этой поставке.';
  saveText = 'Сохранить';
  savingText = 'Сохранение...';
  downloadCIText = 'Скачать CI (PDF)';
  downloadPLText = 'Скачать PL (PDF)';
  fieldSellerNameText = 'Продавец (Seller) — название';
  fieldSellerAddressText = 'Продавец — адрес';
  fieldBuyerNameText = 'Покупатель (Buyer) — название';
  fieldBuyerAddressText = 'Покупатель — адрес';
  fieldShipperNameText = 'Отправитель (Shipper), если не как продавец';
  fieldConsigneeNameText = 'Грузополучатель (Consignee), если не как покупатель';
  fieldInvoiceNumberText = 'Номер инвойса';
  fieldInvoiceDateText = 'Дата';
  fieldYourOrderNoText = 'Ваш заказ №';
  fieldOurOrderNoText = 'Наш заказ №';
  fieldDocumentaryCreditNoText = 'Documentary Credit No.';
  fieldIssuedByBankText = 'Банк (Issued by)';
  fieldVesselAircraftText = 'Транспорт (Vessel/Aircraft/Truck)';
  fieldFromLocationText = 'Откуда (From)';
  fieldToLocationText = 'Куда (To)';
  fieldCountryOfOriginText = 'Страна происхождения товара';
  fieldTermsOfDeliveryText = 'Условия поставки и оплаты (Terms)';
  fieldCurrencyText = 'Валюта';
  placeholderSameAsSellerText = 'Same as Seller';
  placeholderSameAsBuyerText = 'Same as Buyer';
  columnDescriptionText = 'Наименование / HS код';
  columnQuantityText = 'Кол-во (PCS)';
  columnUnitPriceText = 'Цена за ед.';
  columnAmountText = 'Сумма';
  columnCartonsText = 'Мест (CTNS)';
  columnNetWeightText = 'Вес нетто, кг';
  columnGrossWeightText = 'Вес брутто, кг';
  columnVolumeText = 'Объём, м³';
  placeholderDescriptionText = 'Наименование';
  placeholderHsCodeText = 'HS код';
  addRowText = '+ Добавить строку';

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private supplierDocumentService: SupplierDocumentService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['calculationId'] && !changes['calculationId'].firstChange) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.toggleTitleText = this.t('supplierDocument.toggleTitle', 'Документы поставки (CI / PL)');
    this.loadingText = this.t('supplierDocument.loading', 'Загрузка...');
    this.hintEditableText = this.t(
      'supplierDocument.hintEditable',
      'Часть полей заполнена автоматически из расчёта — проверьте и дополните недостающее (вес, количество мест, объём, номера документов), затем сохраните.'
    );
    this.hintReadonlyNotSavedText = this.t('supplierDocument.hintReadonlyNotSaved', 'Поставщик ещё не сохранил документы по этой поставке.');
    this.saveText = this.t('supplierDocument.save', 'Сохранить');
    this.savingText = this.t('supplierDocument.saving', 'Сохранение...');
    this.downloadCIText = this.t('supplierDocument.downloadCI', 'Скачать CI (PDF)');
    this.downloadPLText = this.t('supplierDocument.downloadPL', 'Скачать PL (PDF)');
    this.fieldSellerNameText = this.t('supplierDocument.fieldSellerName', 'Продавец (Seller) — название');
    this.fieldSellerAddressText = this.t('supplierDocument.fieldSellerAddress', 'Продавец — адрес');
    this.fieldBuyerNameText = this.t('supplierDocument.fieldBuyerName', 'Покупатель (Buyer) — название');
    this.fieldBuyerAddressText = this.t('supplierDocument.fieldBuyerAddress', 'Покупатель — адрес');
    this.fieldShipperNameText = this.t('supplierDocument.fieldShipperName', 'Отправитель (Shipper), если не как продавец');
    this.fieldConsigneeNameText = this.t('supplierDocument.fieldConsigneeName', 'Грузополучатель (Consignee), если не как покупатель');
    this.fieldInvoiceNumberText = this.t('supplierDocument.fieldInvoiceNumber', 'Номер инвойса');
    this.fieldInvoiceDateText = this.t('supplierDocument.fieldInvoiceDate', 'Дата');
    this.fieldYourOrderNoText = this.t('supplierDocument.fieldYourOrderNo', 'Ваш заказ №');
    this.fieldOurOrderNoText = this.t('supplierDocument.fieldOurOrderNo', 'Наш заказ №');
    this.fieldDocumentaryCreditNoText = this.t('supplierDocument.fieldDocumentaryCreditNo', 'Documentary Credit No.');
    this.fieldIssuedByBankText = this.t('supplierDocument.fieldIssuedByBank', 'Банк (Issued by)');
    this.fieldVesselAircraftText = this.t('supplierDocument.fieldVesselAircraft', 'Транспорт (Vessel/Aircraft/Truck)');
    this.fieldFromLocationText = this.t('supplierDocument.fieldFromLocation', 'Откуда (From)');
    this.fieldToLocationText = this.t('supplierDocument.fieldToLocation', 'Куда (To)');
    this.fieldCountryOfOriginText = this.t('supplierDocument.fieldCountryOfOrigin', 'Страна происхождения товара');
    this.fieldTermsOfDeliveryText = this.t('supplierDocument.fieldTermsOfDelivery', 'Условия поставки и оплаты (Terms)');
    this.fieldCurrencyText = this.t('supplierDocument.fieldCurrency', 'Валюта');
    this.placeholderSameAsSellerText = this.t('supplierDocument.placeholderSameAsSeller', 'Same as Seller');
    this.placeholderSameAsBuyerText = this.t('supplierDocument.placeholderSameAsBuyer', 'Same as Buyer');
    this.columnDescriptionText = this.t('supplierDocument.columnDescription', 'Наименование / HS код');
    this.columnQuantityText = this.t('supplierDocument.columnQuantity', 'Кол-во (PCS)');
    this.columnUnitPriceText = this.t('supplierDocument.columnUnitPrice', 'Цена за ед.');
    this.columnAmountText = this.t('supplierDocument.columnAmount', 'Сумма');
    this.columnCartonsText = this.t('supplierDocument.columnCartons', 'Мест (CTNS)');
    this.columnNetWeightText = this.t('supplierDocument.columnNetWeight', 'Вес нетто, кг');
    this.columnGrossWeightText = this.t('supplierDocument.columnGrossWeight', 'Вес брутто, кг');
    this.columnVolumeText = this.t('supplierDocument.columnVolume', 'Объём, м³');
    this.placeholderDescriptionText = this.t('supplierDocument.placeholderDescription', 'Наименование');
    this.placeholderHsCodeText = this.t('supplierDocument.placeholderHsCode', 'HS код');
    this.addRowText = this.t('supplierDocument.addRow', '+ Добавить строку');
  }

  private load(): void {
    if (!this.calculationId) return;
    this.loading = true;
    this.error = null;

    this.supplierDocumentService.get(this.calculationId).subscribe({
      next: (doc) => {
        this.document = doc;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading supplier document:', err);
        this.error = this.t('supplierDocument.errorLoad', 'Ошибка при загрузке документа');
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  addItem(): void {
    if (!this.document) return;
    this.document.items = [...this.document.items, {}];
  }

  removeItem(index: number): void {
    if (!this.document) return;
    this.document.items = this.document.items.filter((_, i) => i !== index);
  }

  save(): void {
    if (!this.document || this.saving || this.readonlyMode) return;
    this.saving = true;
    this.error = null;
    this.savedMessage = null;

    this.supplierDocumentService.save(this.calculationId, this.document).subscribe({
      next: (doc) => {
        this.document = doc;
        this.saving = false;
        this.savedMessage = this.t('supplierDocument.saved', 'Сохранено');
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error saving supplier document:', err);
        this.error = err.error?.message || this.t('supplierDocument.errorSave', 'Ошибка при сохранении');
        this.saving = false;
        this.cdr.markForCheck();
      }
    });
  }

  getTotalAmount(): number {
    if (!this.document) return 0;
    return this.document.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  }

  getTotalQuantity(): number {
    if (!this.document) return 0;
    return this.document.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  getTotalCartons(): number {
    if (!this.document) return 0;
    return this.document.items.reduce((sum, item) => sum + (item.cartons || 0), 0);
  }

  getTotalNetWeight(): number {
    if (!this.document) return 0;
    return this.document.items.reduce((sum, item) => sum + (item.netWeightKg || 0), 0);
  }

  getTotalGrossWeight(): number {
    if (!this.document) return 0;
    return this.document.items.reduce((sum, item) => sum + (item.grossWeightKg || 0), 0);
  }

  getTotalVolume(): number {
    if (!this.document) return 0;
    return this.document.items.reduce((sum, item) => sum + (item.volumeCbm || 0), 0);
  }

  downloadCI(): void {
    this.downloadPdf(this.ciTemplate, `CI-${this.document?.invoiceNumber || this.calculationId}.pdf`);
  }

  downloadPL(): void {
    this.downloadPdf(this.plTemplate, `PL-${this.document?.invoiceNumber || this.calculationId}.pdf`);
  }

  private downloadPdf(templateRef: ElementRef, filename: string): void {
    if (!templateRef?.nativeElement) return;
    const opt = {
      margin: [10, 10, 10, 10],
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const h2p = (window as any).html2pdf;
    if (typeof h2p === 'function') {
      h2p().set(opt).from(templateRef.nativeElement).save();
    }
  }
}
